import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  Paper,
  Collapse,
} from '@mui/material';
import {
  Person as UserIcon,
  Source as SourceIcon,
  Download as DownloadIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useChat, Message } from '../contexts/ChatContext';
import ReactMarkdown from 'react-markdown';
import { chatService } from '../services/chatService';
import TicketPreview from './TicketPreview';

const ChatMessages: React.FC = () => {
  const { activeConversation, isTyping, updateMessageFeedback } = useChat();
  const messages = useMemo(() => activeConversation?.messages || [], [activeConversation]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  // Track expanded state for each message's sources
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const theme = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const toggleSources = (messageId: string) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const handleDownloadSource = async (url: string, title: string) => {
    try {
      // Use the secure download endpoint
      const downloadUrl = await chatService.downloadDocument(url);
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = title || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download document:', error);
      // Fallback to opening in new tab if possible (for public docs)
      window.open(url, '_blank');
    }
  };

  const renderMessage = (message: Message) => {
    const isUser = message.role === 'user';
    const hasSources = message.sources && message.sources.length > 0;
    const isSourcesExpanded = expandedSources[message.id] || false;

    return (
      <motion.div
        key={message.id}
        initial={{ opacity: 0, y: 10, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{ width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            gap: 2,
            mb: 4,
            px: { xs: 2, md: 4 },
            maxWidth: '100%',
          }}
        >
          {/* Avatar */}
          <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: isUser 
              ? 'transparent' 
              : 'transparent',
            border: isUser 
              ? `1px solid ${theme.palette.divider}` 
              : 'none',
            boxShadow: isUser ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.2)',
            background: !isUser ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : undefined,
          }}
        >
          {isUser ? (
            <UserIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
          )}
        </Avatar>

        {/* Message Content */}
        <Box sx={{ 
          flex: 1, 
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {isUser ? 'You' : 'CoreSupport AI'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: isUser ? 2 : 0, // No padding for AI (markdown handles it or transparent)
              bgcolor: isUser 
                ? theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : '#eff6ff'
                : 'transparent',
              borderRadius: isUser ? '20px 20px 4px 20px' : 0,
              maxWidth: '100%',
              width: isUser ? 'fit-content' : '100%',
              border: isUser ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}` : 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: isUser 
                  ? theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : '#e0e7ff'
                  : 'transparent',
              }
            }}
          >
            {isUser ? (
              <Box>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary, whiteSpace: 'pre-wrap' }}>
                  {message.content}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  mt: 0.5,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  '.MuiPaper-root:hover &': { opacity: 0.7 }
                }}>
                  <Tooltip title={copiedId === message.id ? "Copied!" : "Copy"}>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(message.content);
                        setCopiedId(message.id);
                        setTimeout(() => setCopiedId(null), 2000);
                      }}
                      sx={{ 
                        p: 0.5,
                        color: theme.palette.text.secondary,
                        '&:hover': { color: theme.palette.primary.main, bgcolor: 'transparent' }
                      }}
                    >
                      {copiedId === message.id ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                typography: 'body1',
                color: theme.palette.text.primary,
                '& p': { 
                  mb: 1.5, 
                  lineHeight: 1.7,
                  '&:first-of-type': { mt: 0 },
                  '&:last-child': { mb: 0 }
                },
                '& ul, & ol': { 
                  pl: 3, 
                  mb: 1.5,
                  mt: 0.5,
                  '& ul, & ol': { mt: 0.5, mb: 0.5 }
                },
                '& li': { 
                  mb: 0.75,
                  lineHeight: 1.6,
                  '&::marker': { 
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  }
                },
                '& strong': { fontWeight: 600, color: theme.palette.text.primary },
                '& em': { fontStyle: 'italic' },
                '& code': {
                  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                  fontSize: '0.85em',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  px: 0.6,
                  py: 0.2,
                  borderRadius: 1,
                },
                '& pre': {
                  bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8fafc',
                  p: 2,
                  borderRadius: 3,
                  overflowX: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  my: 2,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '& code': {
                    bgcolor: 'transparent',
                    p: 0,
                    fontSize: '0.85em',
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#334155',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontWeight: 700,
                  mt: 3,
                  mb: 1.5,
                  color: theme.palette.text.primary,
                  '&:first-of-type': { mt: 0 }
                },
                '& h1': { fontSize: '1.8rem' },
                '& h2': { fontSize: '1.5rem' },
                '& h3': { fontSize: '1.2rem' },
                '& h4': { fontSize: '1.1rem' },
                '& hr': { 
                  border: 'none', 
                  borderTop: `1px solid ${theme.palette.divider}`, 
                  my: 2 
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  m: 0,
                  pl: 2,
                  py: 0.5,
                  my: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                  borderRadius: '0 4px 4px 0',
                },
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': { 
                    textDecoration: 'underline',
                    opacity: 0.8
                  }
                },
                '& table': { 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  my: 2,
                  border: `1px solid ${theme.palette.divider}`
                },
                '& th, & td': { 
                  border: `1px solid ${theme.palette.divider}`, 
                  p: 1.5, 
                  textAlign: 'left' 
                },
                '& th': { 
                  bgcolor: theme.palette.action.hover,
                  fontWeight: 600
                }
              }}>
                {!message.content && !message.ticketPreview ? (
                  <Box sx={{ display: 'flex', gap: 0.5, p: 1, alignItems: 'center', height: 24 }}>
                    <Box sx={{ width: 6, height: 6, bgcolor: theme.palette.primary.main, borderRadius: '50%', animation: 'pulse 1s infinite' }} />
                    <Box sx={{ width: 6, height: 6, bgcolor: theme.palette.primary.main, borderRadius: '50%', animation: 'pulse 1s infinite 0.2s' }} />
                    <Box sx={{ width: 6, height: 6, bgcolor: theme.palette.primary.main, borderRadius: '50%', animation: 'pulse 1s infinite 0.4s' }} />
                  </Box>
                ) : (
                  <>
                    <ReactMarkdown>
                      {(() => {
                        let content = message.content;
                        // Filter out JSON if ticket preview exists
                        if (message.ticketPreview) {
                          content = content.split('\n').filter(line => !line.trim().startsWith('{')).join('\n');
                        }
                        // Clean up excessive newlines (3+ becomes 2) and trim
                        return content.replace(/\n{3,}/g, '\n\n').trim();
                      })()}
                    </ReactMarkdown>
                    
                    {message.ticketPreview && (
                      <TicketPreview ticket={message.ticketPreview} />
                    )}
                  </>
                )}
              </Box>
            )}
          </Paper>

          {/* AI Message Extras */}
          {!isUser && (
            <Box sx={{ width: '100%', mt: 1 }}>
              {/* Collapsible Sources */}
              {hasSources && (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Button
                    onClick={() => toggleSources(message.id)}
                    startIcon={<SourceIcon sx={{ fontSize: 16 }} />}
                    endIcon={isSourcesExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    size="small"
                    sx={{ 
                      textTransform: 'none', 
                      color: theme.palette.text.secondary,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      py: 0.5,
                      px: 1,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: theme.palette.action.hover,
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {message.sources?.length || 0} Source{(message.sources?.length || 0) !== 1 ? 's' : ''} Used
                  </Button>
                  
                  <Collapse in={isSourcesExpanded}>
                    <Box sx={{ 
                      mt: 1, 
                      pl: 1, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1 
                    }}>
                      {message.sources?.map((source, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            p: 1,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            maxWidth: '100%'
                          }}
                        >
                          <SourceIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ 
                            flex: 1, 
                            fontSize: '0.85rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: theme.palette.text.primary
                          }}>
                            {source.title}
                          </Typography>
                          {source.url && (
                            <Tooltip title="Download">
                              <IconButton 
                                size="small"
                                onClick={() => handleDownloadSource(source.url!, source.title)}
                                sx={{ 
                                  p: 0.5,
                                  color: theme.palette.primary.main,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                                  '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.15)',
                                  }
                                }}
                              >
                                <DownloadIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Suggested Actions */}
              {message.suggestedActions && message.suggestedActions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {message.suggestedActions.map((action, idx) => (
                      <Button
                        key={idx}
                        variant="outlined"
                        size="small"
                        onClick={() => console.log('Action clicked:', action)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          bgcolor: theme.palette.background.paper,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: theme.palette.action.hover
                          }
                        }}
                      >
                        {action.description}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Feedback & Actions Bar */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mt: 1.5, 
                opacity: 0.7,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 }
              }}>
                <Tooltip title={copiedId === message.id ? "Copied!" : "Copy"}>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      navigator.clipboard.writeText(message.content);
                      setCopiedId(message.id);
                      setTimeout(() => setCopiedId(null), 2000);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: theme.palette.action.hover }
                    }}
                  >
                    {copiedId === message.id ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Helpful">
                  <IconButton 
                    size="small"
                    onClick={() => updateMessageFeedback(message.id, { liked: true, disliked: false })}
                    color={message.feedback?.liked ? 'primary' : 'default'}
                    sx={{
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: theme.palette.action.hover }
                    }}
                  >
                    <ThumbUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Not helpful">
                  <IconButton 
                    size="small"
                    onClick={() => updateMessageFeedback(message.id, { liked: false, disliked: true })}
                    color={message.feedback?.disliked ? 'error' : 'default'}
                    sx={{
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: theme.palette.action.hover }
                    }}
                  >
                    <ThumbDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      </motion.div>
    );
  };

  return (
    <Box sx={{ 
      flex: 1, 
      overflowY: 'auto', 
      py: 4,
      display: 'flex', 
      flexDirection: 'column',
    }}>
      <AnimatePresence initial={false}>
        {messages.map(renderMessage)}
      </AnimatePresence>
      
      <div ref={messagesEndRef} />
      
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.4; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </Box>
  );
};

export default ChatMessages;
